import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

// ============================================
// Button Component - Modern Design System
// ============================================

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = cn(
      'inline-flex items-center justify-center font-medium',
      'rounded-lg transition-all duration-200 ease-out',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'active:scale-[0.98]',
      fullWidth && 'w-full'
    );

    const variantStyles = {
      primary: cn(
        'bg-primary text-white',
        'hover:bg-primary-600 hover:shadow-lg hover:shadow-primary/20',
        'focus-visible:ring-primary/50 focus-visible:ring-offset-background',
        'active:bg-primary-700'
      ),
      secondary: cn(
        'bg-surface-elevated text-text',
        'border border-border',
        'hover:bg-surface-elevated/80 hover:border-border-hover',
        'focus-visible:ring-border'
      ),
      outline: cn(
        'bg-transparent text-text',
        'border border-border',
        'hover:bg-surface-elevated hover:border-primary/50 hover:text-primary',
        'focus-visible:ring-primary/50'
      ),
      ghost: cn(
        'bg-transparent text-text-muted',
        'hover:bg-surface-elevated hover:text-text',
        'focus-visible:ring-border'
      ),
      danger: cn(
        'bg-red-500 text-white',
        'hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/20',
        'focus-visible:ring-red-500/50',
        'active:bg-red-700'
      ),
      success: cn(
        'bg-green-500 text-white',
        'hover:bg-green-600 hover:shadow-lg hover:shadow-green-500/20',
        'focus-visible:ring-green-500/50',
        'active:bg-green-700'
      ),
    };

    const sizeStyles = {
      xs: 'h-7 px-2.5 text-xs gap-1',
      sm: 'h-8 px-3 text-xs gap-1.5',
      md: 'h-9 px-4 text-sm gap-2',
      lg: 'h-10 px-5 text-sm gap-2',
      xl: 'h-11 px-6 text-base gap-2.5',
    };

    const iconSizeMap = {
      xs: 'h-3 w-3',
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-4 w-4',
      xl: 'h-5 w-5',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <Loader2 className={cn(iconSizeMap[size], 'animate-spin')} />
        ) : (
          <>
            {leftIcon && <span className={cn('shrink-0', iconSizeMap[size])}>{leftIcon}</span>}
            {children}
            {rightIcon && <span className={cn('shrink-0', iconSizeMap[size])}>{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// ============================================
// Icon Button Component
// ============================================

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant = 'ghost',
      size = 'md',
      isLoading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles = cn(
      'inline-flex items-center justify-center',
      'rounded-lg transition-all duration-200 ease-out',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
      'active:scale-95'
    );

    const variantStyles = {
      primary: cn(
        'bg-primary text-white',
        'hover:bg-primary-600',
        'focus-visible:ring-primary/50'
      ),
      secondary: cn(
        'bg-surface-elevated text-text',
        'border border-border',
        'hover:bg-surface-elevated/80'
      ),
      outline: cn(
        'bg-transparent text-text-muted',
        'border border-border',
        'hover:bg-surface-elevated hover:text-text'
      ),
      ghost: cn(
        'bg-transparent text-text-muted',
        'hover:bg-surface-elevated hover:text-text'
      ),
      danger: cn(
        'bg-transparent text-red-500',
        'hover:bg-red-500/10'
      ),
    };

    const sizeStyles = {
      xs: 'h-7 w-7',
      sm: 'h-8 w-8',
      md: 'h-9 w-9',
      lg: 'h-10 w-10',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variantStyles[variant], sizeStyles[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
